import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  AppBar,
  Toolbar,
  CssBaseline,
  Link,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  ThemeProvider,
  Badge,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import SecurityIcon from '@mui/icons-material/Security';
import MenuIcon from '@mui/icons-material/Menu';
import BuildIcon from '@mui/icons-material/Build';
import SettingsIcon from '@mui/icons-material/Settings';
import LockIcon from '@mui/icons-material/Lock';
import PaletteIcon from '@mui/icons-material/Palette';
import HubIcon from '@mui/icons-material/Hub';

import IssueCredentialTab from './components/IssueCredentialTab';
import VerifyCredentialTab from './components/VerifyCredentialTab';
import Dashboard from './components/Dashboard';
import ManageCredentials from './components/ManageCredentials';
import CredentialRequests from './components/CredentialRequests';
import { CredentialManagerPage } from './components/CredentialManager';
import CredentialDiscovery from './components/CredentialDiscovery';
import DesignSystem from './components/DesignSystem';
import Members from './components/Members';
import Requests from './components/Requests';
import InboxIcon from '@mui/icons-material/Inbox';
import { SignedIn, SignedOut, SignInButton, UserButton } from '@clerk/clerk-react';
import { checkServiceStatus, getAppConfig, login as apiLogin } from './services/api';
import figmaDarkTheme from './theme/figmaDarkTheme';

// Clerk gates privileged actions when a publishable key is present; otherwise
// the console falls back to the legacy operator login.
const clerkEnabled = !!process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// Use the Figma-inspired dark theme
const theme = figmaDarkTheme;

// Common styles with figma aesthetics
const paperStyle = {
  p: 4, 
  borderRadius: 8,
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.4)',
  border: '1px solid #3D3D3D',
  backgroundColor: '#2C2C2C',
  backgroundImage: 'none',
  width: '100%'
};

// Tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vc-tabpanel-${index}`}
      aria-labelledby={`vc-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function App() {
  const [tabValue, setTabValue] = useState(6); // open on the Members list
  const [serviceStatus, setServiceStatus] = useState({
    isOnline: false,
    message: 'Checking...'
  });
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [brandName, setBrandName] = useState('Noosphere Credentials Manager');
  const [brandLogo, setBrandLogo] = useState('');
  const [authed, setAuthed] = useState(!!localStorage.getItem('vc_admin_token'));
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginErr, setLoginErr] = useState(null);
  const [loginBusy, setLoginBusy] = useState(false);

  const handleLogin = async () => {
    try {
      setLoginBusy(true); setLoginErr(null);
      const res = await apiLogin(loginUser, loginPass);
      if (res && res.token) {
        localStorage.setItem('vc_admin_token', res.token);
        setAuthed(true); setLoginOpen(false); setLoginPass('');
      } else {
        setLoginErr('Login failed');
      }
    } catch (e) {
      setLoginErr(e.response?.data?.error || 'Login failed. Check your credentials.');
    } finally {
      setLoginBusy(false);
    }
  };
  const handleLogout = () => { localStorage.removeItem('vc_admin_token'); setAuthed(false); };

  // Theme for responsive design
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const status = await checkServiceStatus();
        setServiceStatus({
          isOnline: status,
          message: status ? 'Online' : 'Offline'
        });
      } catch (error) {
        setServiceStatus({
          isOnline: false,
          message: 'Offline'
        });
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Load per-customer branding from the backend (white-label).
  useEffect(() => {
    getAppConfig()
      .then((cfg) => {
        if (cfg && cfg.brandName) {
          setBrandName(cfg.brandName);
          document.title = cfg.brandName;
        }
        if (cfg && cfg.brandLogo) setBrandLogo(cfg.brandLogo);
      })
      .catch(() => {});
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Menu handlers for mobile
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  const handleMenuSelect = (value) => {
    setTabValue(value);
    handleMenuClose();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar position="static" elevation={0} sx={{ 
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
        backgroundColor: '#2C2C2C',
        color: '#FFFFFF'
      }}>
        <Toolbar>
          {brandLogo
            ? <Box sx={{ mr: 2, bgcolor: '#fff', borderRadius: 1, px: 1, py: 0.5, display: 'flex', alignItems: 'center' }}>
                <img src={brandLogo} alt={brandName} style={{ height: 24, display: 'block' }} />
              </Box>
            : <VerifiedIcon sx={{ mr: 2, fontSize: 24 }} />}
          <Typography variant="h6" component="div" sx={{
            flexGrow: 1,
            fontWeight: 600,
            letterSpacing: '-0.01em'
          }}>
            {brandName}
          </Typography>
          
          {isMobile ? (
            <>
              <IconButton
                color="inherit"
                onClick={handleMenuOpen}
                edge="end"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={() => handleMenuSelect(0)}>Dashboard</MenuItem>
                <MenuItem onClick={() => handleMenuSelect(2)}>Manage Credentials</MenuItem>
                <MenuItem onClick={() => handleMenuSelect(3)}>Signing Credentials</MenuItem>
                <MenuItem onClick={() => handleMenuSelect(6)}>Members</MenuItem>
                <MenuItem onClick={() => handleMenuSelect(5)}>Requests</MenuItem>
              </Menu>
            </>
          ) : (
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              textColor="inherit"
              sx={{ 
                ml: 4,
                '& .MuiTab-root': {
                  minWidth: 100,
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  transition: 'all 0.2s',
                  '&:hover': {
                    opacity: 0.8,
                  },
                },
                '& .Mui-selected': {
                  color: '#ffffff',
                  fontWeight: 600,
                },
                '& .MuiTabs-indicator': {
                  height: 2,
                  backgroundColor: '#ffffff',
                },
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Dashboard" value={0} />
              <Tab label="Manage Credentials" value={2} />
              <Tab label="Signing Credentials" value={3} icon={<LockIcon />} iconPosition="start" />
              <Tab label="Members" value={6} icon={<HubIcon />} iconPosition="start" />
              <Tab label="Requests" value={5} icon={<InboxIcon />} iconPosition="start" />
            </Tabs>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
            <Tooltip title={`Service Status: ${serviceStatus.message}`}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                '& .status-indicator': {
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  marginRight: '8px'
                },
                '@keyframes statusPulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.3 },
                  '100%': { opacity: 1 },
                },
                '& .status-online': {
                  backgroundColor: '#76B900',
                  animation: 'statusPulse 2s ease-in-out infinite'
                },
                '& .status-offline': {
                  backgroundColor: '#8B0000'
                }
              }}>
                <span className={`status-indicator status-${serviceStatus.isOnline ? 'online' : 'offline'}`}></span>
                {!isMobile && <Typography variant="body2" component="span">Service: {serviceStatus.message}</Typography>}
              </Box>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2, gap: 1 }}>
            {clerkEnabled ? (
              <>
                <SignedOut>
                  <SignInButton mode="modal">
                    <Button color="inherit" variant="outlined" size="small">Sign in</Button>
                  </SignInButton>
                </SignedOut>
                <SignedIn>
                  <UserButton afterSignOutUrl="/" />
                </SignedIn>
              </>
            ) : authed ? (
              <>
                <Chip size="small" color="success" variant="outlined" label="Signed in" />
                <Button color="inherit" size="small" onClick={handleLogout}>Log out</Button>
              </>
            ) : (
              <Button color="inherit" variant="outlined" size="small" onClick={() => setLoginOpen(true)}>Log in</Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Dialog open={loginOpen} onClose={() => setLoginOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Operator sign in</DialogTitle>
        <DialogContent>
          {loginErr && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{loginErr}</Typography>}
          <TextField autoFocus margin="dense" label="Username" fullWidth value={loginUser}
            onChange={(e) => setLoginUser(e.target.value)} />
          <TextField margin="dense" label="Password" type="password" fullWidth value={loginPass}
            onChange={(e) => setLoginPass(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoginOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleLogin} disabled={loginBusy}>Sign in</Button>
        </DialogActions>
      </Dialog>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, flex: '1 0 auto' }}>
        {/* Dashboard Tab */}
        <TabPanel value={tabValue} index={0}>
          <Dashboard onNavigate={setTabValue} />
        </TabPanel>

        {/* Credential Discovery Tab */}
        {/* Manage Credentials Tab */}
        <TabPanel value={tabValue} index={2}>
          <Paper elevation={1} sx={paperStyle}>
            <Typography variant="h4" align="center" gutterBottom>
              Manage Credentials
            </Typography>
            <Typography variant="subtitle1" align="center" color="text.secondary" paragraph>
              Define credential types, claims, and attributes
            </Typography>

            <Divider sx={{ mb: 4 }} />

            <ManageCredentials />
          </Paper>
        </TabPanel>

        {/* Signing Credentials Tab */}
        <TabPanel value={tabValue} index={3}>
          <Paper elevation={1} sx={paperStyle}>
            <Typography variant="h4" align="center" gutterBottom>
              Signing Credentials
            </Typography>
            <Typography variant="subtitle1" align="center" color="text.secondary" paragraph>
              Manage keys and certificates used for signing verifiable credentials
            </Typography>

            <Divider sx={{ mb: 4 }} />

            <CredentialManagerPage />
          </Paper>
        </TabPanel>


        {/* Credential Requester Tab */}
        <TabPanel value={tabValue} index={6}>
          <Paper elevation={1} sx={paperStyle}>
            <Members />
          </Paper>
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <Paper elevation={1} sx={paperStyle}>
            <Requests />
          </Paper>
        </TabPanel>
      </Container>
      
      <Box component="footer" sx={{ 
        py: 3, 
        px: 2, 
        mt: 'auto', 
        backgroundColor: '#1A1A1A',
        borderTop: '1px solid #3D3D3D'
      }}>
        <Container maxWidth="sm">
          <Typography variant="body2" color="text.secondary" align="center" sx={{ fontWeight: 500 }}>
            {'© 2026 Noosphere Technologies, Inc. | '}
            <Link color="inherit" href="https://noosphere.tech" target="_blank" rel="noopener">
              Noosphere.tech
            </Link>
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

function AppWrapper() {
  return (
    <ThemeProvider theme={theme}>
      <App />
    </ThemeProvider>
  );
}

export default AppWrapper;